import { createContext, useContext, useState, useEffect } from 'react';
import { validateToken, TokenValidationResponse } from '../api/validateToken';

interface AuthorizationContextI {
  isAuthenticated: boolean;
  handleJWTChange: (jwt: string) => void;
  sessionExpired: () => void;
  authorizationToken: string;
  currentWarehouse: string;
}

const AuthorizationContext = createContext({} as AuthorizationContextI);
const useAuthorization = () => useContext(AuthorizationContext);

interface JWTData {
  warehouse?: string;
  exp?: number;
}

const decodeJWTPayload = (token: string): JWTData => {
  try {
    return JSON.parse(atob(token.split('.')[1])) as JWTData;
  } catch (e) {
    return {};
  }
};

export const isValidToken = (token: string): boolean => {
  if (!token) return false;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    const payload = JSON.parse(atob(parts[1])) as JWTData;

    if (!payload.exp || !payload.warehouse) return false;

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp > currentTime;
  } catch (e) {
    return false;
  }
};

const isTokenValidationResponse = (
  response: any
): response is TokenValidationResponse => {
  return (
    response &&
    typeof response === 'object' &&
    'valid' in response &&
    'userName' in response &&
    'warehouseId' in response &&
    'expiresAt' in response &&
    typeof response.valid === 'boolean' &&
    typeof response.userName === 'string' &&
    typeof response.warehouseId === 'string' &&
    typeof response.expiresAt === 'number'
  );
};

const AuthorizationContextProvider = (props: any) => {
  const jwtToken = window.localStorage.getItem('lyne-sortation-token');
  const isValid = isValidToken(jwtToken || '');

  const currentWarehouse: string =
    // eslint-disable-next-line react/destructuring-assignment
    props.currentWarehouse || decodeJWTPayload(jwtToken || '').warehouse || '';

  const [isAuthenticated, setIsAuthenticated] = useState(() => isValid);

  const handleJWTChange = (jwt: string) => {
    window.localStorage.setItem('lyne-sortation-token', jwt);
    setIsAuthenticated(true);
  };

  const sessionExpired = () => {
    window.localStorage.removeItem('lyne-sortation-token');
    setIsAuthenticated(false);
  };

  useEffect(() => {
    let isMounted = true;

    const validateTokenWithServer = async () => {
      if (!isMounted) return;

      if (!jwtToken) {
        if (isMounted) setIsAuthenticated(false);
        return;
      }

      // Client-side validation
      if (!isValid) {
        console.warn('JWT token is expired or invalid, cleaning up...');
        window.localStorage.removeItem('lyne-sortation-token');
        if (isMounted) setIsAuthenticated(false);
        return;
      }

      // Server-side validation
      try {
        const validationResult = await validateToken(jwtToken);

        if (!isMounted) return;

        // Server validation failure
        if ('message' in validationResult) {
          window.localStorage.removeItem('lyne-sortation-token');
          setIsAuthenticated(false);
          return;
        }

        if (
          isTokenValidationResponse(validationResult) &&
          validationResult.valid === true
        ) {
          setIsAuthenticated(true);
        } else {
          console.warn('Server token validation failed:', validationResult);
          window.localStorage.removeItem('lyne-sortation-token');
          setIsAuthenticated(false);
        }
      } catch (error) {
        if (!isMounted) return;

        window.localStorage.removeItem('lyne-sortation-token');
        setIsAuthenticated(false);
      }
    };

    validateTokenWithServer();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [jwtToken, isValid]);

  return (
    <AuthorizationContext.Provider
      value={{
        isAuthenticated,
        handleJWTChange,
        sessionExpired,
        authorizationToken: jwtToken || '',
        currentWarehouse,
      }}
    >
      {/* eslint-disable-next-line react/destructuring-assignment */}
      {props.children}
    </AuthorizationContext.Provider>
  );
};

export { AuthorizationContextProvider, useAuthorization, AuthorizationContext };
